import type { childPartV2 } from "@/api/breakdownService";
import { TableCell, TableRow } from "@mui/material";
import ChildPartData from "./ChildPartData";

interface ChildPartProcessV2DataProps {
  row: childPartV2;
  version: "v1" | "v2";
  isSelected: boolean;
  onSelectChange: (row: childPartV2, checked: boolean) => void;
  isAdding: boolean;
  isEditing: boolean;
}

const ChildPartProcessV2Data = ({
  row,
  version,
  isSelected,
  onSelectChange,
  isAdding,
  isEditing,
}: ChildPartProcessV2DataProps) => {
  const allCpProcesses = row.child_part_process.cp_process || [];
  const cpProcesses = allCpProcesses.filter(
    (process) =>
      process.qty_1 != 0 ||
      process.qty_2 != 0 ||
      process.cost_per_qty_1 != 0 ||
      process.cost_per_qty_2 != 0,
  );
  const processCount = cpProcesses.length > 0 ? cpProcesses.length : 1;

  if (cpProcesses.length === 0) {
    return (
      <TableRow>
        <ChildPartData
          version={version}
          row={row}
          processCount={1}
          isSelected={isSelected}
          onSelectChange={onSelectChange}
          isAdding={isAdding}
          isEditing={isEditing}
        />
        <TableCell
          sx={{
            minWidth: 180,
            maxWidth: 180,
            borderRight: "1px solid #e0e0e0",
            borderLeft: "1px solid #e0e0e0",
            textAlign: "right",
            fontSize: "0.9rem",
          }}
        >
          {Number(row.child_part_process.cp_process_total ?? 0).toFixed(2)}
        </TableCell>
        <TableCell
          sx={{
            color: "#888888",
            borderRight: "1px solid #e0e0e0",
            borderLeft: "1px solid #e0e0e0",
            textAlign: "left",
            fontSize: "0.9rem",
          }}
          colSpan={11}
        >
          No Process
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {cpProcesses.map((process, index) => (
          <TableRow
            key={index}
            sx={{
              backgroundColor: "white",
            }}
          >
            {index === 0 && (
              <>
                <ChildPartData
                  version={version}
                  row={row}
                  processCount={processCount}
                  isSelected={isSelected}
                  onSelectChange={onSelectChange}
                  isAdding={isAdding}
                  isEditing={isEditing}
                />
                <TableCell
                  sx={{
                    minWidth: 180,
                    maxWidth: 180,
                    borderRight: "1px solid #e0e0e0",
                    borderLeft: "1px solid #e0e0e0",
                    textAlign: "right",
                    fontSize: "0.9rem",
                  }}
                  rowSpan={processCount}
                >
                  {Number(row.child_part_process.cp_process_total ?? 0).toFixed(2)}
                </TableCell>
                <TableCell
                  sx={{
                    minWidth: 120,
                    maxWidth: 120,
                    borderRight: "1px solid #e0e0e0",
                    borderLeft: "1px solid #e0e0e0",
                    textAlign: "right",
                    fontSize: "0.9rem",
                  }}
                  rowSpan={processCount}
                >
                  {Number(row.child_part_process.cp_process_cost ?? 0).toFixed(2)}
                </TableCell>
              </>
            )}
            <TableCell
              sx={{
                minWidth: 120,
                maxWidth: 120,
                borderRight: "1px solid #e0e0e0",
                borderLeft: "1px solid #e0e0e0",
                textAlign: "right",
                fontSize: "0.9rem",
              }}
            >
              {Number(process.total_cost ?? 0).toFixed(2)}
            </TableCell>
            <TableCell
              sx={{
                minWidth: 200,
                maxWidth: 200,
                borderRight: "1px solid #e0e0e0",
                borderLeft: "1px solid #e0e0e0",
                textAlign: "left",
                fontSize: "0.9rem",
              }}
            >
              {process.part_process_v2_name}
            </TableCell>
            <TableCell
              sx={{
                minWidth: 120,
                maxWidth: 120,
                borderRight: "1px solid #e0e0e0",
                borderLeft: "1px solid #e0e0e0",
                textAlign: "left",
                fontSize: "0.9rem",
              }}
            >
              {process.detail}
            </TableCell>
            <TableCell
              sx={{
                minWidth: 120,
                maxWidth: 120,
                borderRight: "1px solid #e0e0e0",
                borderLeft: "1px solid #e0e0e0",
                textAlign: "right",
                fontSize: "0.9rem",
              }}
            >
              {Number(process.qty_1 ?? 0).toFixed(3)}
            </TableCell>
            <TableCell
              sx={{
                minWidth: 120,
                maxWidth: 120,
                borderRight: "1px solid #e0e0e0",
                borderLeft: "1px solid #e0e0e0",
                textAlign: "right",
                fontSize: "0.9rem",
              }}
            >
              {Number(process.cost_per_qty_1 ?? 0).toFixed(3)}
            </TableCell>
            <TableCell
              sx={{
                minWidth: 120,
                maxWidth: 120,
                borderRight: "1px solid #e0e0e0",
                borderLeft: "1px solid #e0e0e0",
                textAlign: "right",
                fontSize: "0.9rem",
              }}
            >
              {Number(process.qty_2 ?? 0).toFixed(3)}
            </TableCell>
            <TableCell
              sx={{
                minWidth: 120,
                maxWidth: 120,
                borderRight: "1px solid #e0e0e0",
                borderLeft: "1px solid #e0e0e0",
                textAlign: "right",
                fontSize: "0.9rem",
              }}
            >
              {Number(process.cost_per_qty_2 ?? 0).toFixed(3)}
            </TableCell>
            <TableCell
              sx={{
                minWidth: 120,
                maxWidth: 120,
                borderRight: "1px solid #e0e0e0",
                borderLeft: "1px solid #e0e0e0",
                textAlign: "left",
                fontSize: "0.9rem",
              }}
            >
              {process.tooling_name}
            </TableCell>
            <TableCell
              sx={{
                minWidth: 120,
                maxWidth: 120,
                borderRight: "1px solid #e0e0e0",
                borderLeft: "1px solid #e0e0e0",
                textAlign: "right",
                fontSize: "0.9rem",
              }}
            >
              {Number(process.tooling_cost ?? 0).toFixed(2)}
            </TableCell>
          </TableRow>
        ))}
    </>
  );
};

export default ChildPartProcessV2Data;
